import React, { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { X } from 'lucide-react';
import { C, MobileSheet } from './MobileShell';

export default function QRScanner({ onResult, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let stream = null;
    let raf = null;
    let stopped = false;

    const tick = () => {
      if (stopped) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const qr = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
        if (qr?.data) {
          stopped = true;
          onResult(qr.data);
          return;
        }
      }
      raf = requestAnimationFrame(tick);
    };

    const openCamera = navigator.mediaDevices?.getUserMedia?.bind(navigator.mediaDevices);
    if (!openCamera) {
      // iOS blendet die Schnittstelle aus, solange die App keine Kamera-Freigabe
      // erklärt. Der direkte Zugriff wirft dann und reißt die ganze Ansicht mit.
      setError('Die Kamera ist in dieser App nicht verfügbar. Bitte Kennung und PIN manuell eingeben.');
      return undefined;
    }

    openCamera({ video: { facingMode: 'environment' } })
      .then((s) => {
        if (stopped || !videoRef.current) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        videoRef.current.srcObject = s;
        videoRef.current.setAttribute('playsinline', 'true');
        try {
          const p = videoRef.current.play();
          if (p?.catch) p.catch(() => {});
        } catch (_e) { /* Abspielen ist für die Auswertung nicht nötig. */ }
        raf = requestAnimationFrame(tick);
      })
      .catch(() => {
        if (!stopped) {
          setError('Kamera-Zugriff nicht möglich. Bitte Berechtigung erteilen oder Kennung + PIN manuell eingeben.');
        }
      });

    return () => {
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [onResult]);

  return (
    <MobileSheet bare className="fixed inset-0 z-50 flex flex-col" style={{ background: '#000' }} data-testid="mobile-qr-scanner">
      <div className="flex items-center justify-between px-4 py-3" style={{ background: C.bg }}>
        <p className="text-sm font-semibold" style={{ color: C.text }}>QR-Code scannen</p>
        <button onClick={onClose} data-testid="mobile-qr-close"><X className="w-6 h-6" style={{ color: C.text }} /></button>
      </div>
      {error ? (
        <p className="m-auto px-8 text-center text-sm" style={{ color: C.dim }} data-testid="mobile-qr-error">{error}</p>
      ) : (
        <div className="flex-1 relative overflow-hidden">
          <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" muted />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-56 h-56 rounded-2xl" style={{ border: `3px solid ${C.red}`, boxShadow: '0 0 0 4000px rgba(0,0,0,0.45)' }} />
          </div>
          <p className="absolute bottom-8 inset-x-0 text-center text-xs" style={{ color: '#fff' }}>
            Halte den QR-Code vom Kennungs-Zettel in den Rahmen
          </p>
        </div>
      )}
      <canvas ref={canvasRef} className="hidden" />
    </MobileSheet>
  );
}
